import { Magic } from "magic-sdk"
import { useEffect, useState } from "react"
import { useNavigate } from "react-router-dom"
import { Box, HStack, Flex, Link } from "@chakra-ui/react"
import { Text } from "@/components/ui"
import { useAuth } from "@/contexts"
import { useAuthClient, useResendLoginOtp, useVerifyOtp } from "@/hooks"
import { DealStep } from "@/types"
import { CodeTarget, codeTargetFrom } from "@/utils"
import { MfaComponent, SignInComponent } from "./components"

const TYPE_CLIENT = "client"
const TYPE_DEAL_MANAGER = "deal_manager"

export const LoginComponent = () => {
  const magic = new Magic(process.env.REACT_APP_MAGIC_API_KEY ?? "")
  const { user: authUser, refreshUser } = useAuth()
  const navigate = useNavigate()

  const { mutateAsync: authenticate, data: user, isError, error } = useAuthClient()
  const { mutateAsync: verifyOtpMutate, isPending, isSuccess } = useVerifyOtp()
  const { mutateAsync: resendOtp, isPending: isResending } = useResendLoginOtp()

  const [email, setEmail] = useState("")
  const [isLoading, setIsLoading] = useState(false)
  const [mfaStep, setMfaStep] = useState(false)
  const [userId, setUserId] = useState<number | null>(null)
  const [otp, setOtp] = useState("")
  const [userType, setUserType] = useState<string>(TYPE_CLIENT)
  // Where the code went: a text (our number or Twilio Verify) or, when no text can go, email.
  const [codeTarget, setCodeTarget] = useState<CodeTarget>({ channel: "sms", destination: "" })
  const [pendingRedirect, setPendingRedirect] = useState<string | null>(null)
  const [loginError, setLoginError] = useState<string | null>(null)

  // Navigate only after the auth context has the user
  useEffect(() => {
    if (pendingRedirect && authUser) {
      navigate(pendingRedirect)
      setPendingRedirect(null)
    }
  }, [pendingRedirect, authUser])

  useEffect(() => {
    if (user) {
      setIsLoading(false)
    }
  }, [user])

  useEffect(() => {
    if (isError) {
      console.error("Authentication error:", error)
      setIsLoading(false)
    }
  }, [isError, error])

  const handleLogin = async () => {
    setIsLoading(true)
    setLoginError(null)
    try {
      const didToken = await magic.auth.loginWithMagicLink({ email })
      if (!didToken) {
        console.error("Failed to get DID token")
        setLoginError("We couldn't start the sign-in. Please try again.")
        setIsLoading(false)
        return
      }

      const result = await authenticate(didToken)
      if (result.mfaRequired) {
        // Either returning client or deal manager
        setUserId(result.userId);
        setCodeTarget(codeTargetFrom(result));
        setMfaStep(true);
        setIsLoading(false);
        setUserType(result.userType || TYPE_CLIENT);
      } else {
        // No MFA required — either a client with 2FA disabled or a DM with SKIP_MFA
        await refreshUser(result);
        setPendingRedirect(
          result.userType === TYPE_DEAL_MANAGER ? "/eso/deals" : getClientRedirect(result)
        );
      }
    } catch (error) {
      console.error("Login error:", error)
      // The API answers an unreachable phone with 400 + { smsUnreachable: true, error }.
      // Show that message: it is the only signal the client gets, and the alternative
      // (what this did before) is a spinner that stops and no explanation at all.
      const data = (error as { response?: { data?: { error?: string } } })?.response?.data
      setLoginError(
        data?.error ||
        "We couldn't sign you in. Please try again, or contact your ESO representative."
      )
      setIsLoading(false)
    }
  }

  const getClientRedirect = (user: { deals?: { currentStep?: string }[] }) => {
    const deals = user?.deals
    if (!deals || deals.length === 0) return "/nda"
    const hasNdaStep = deals.some(d => d.currentStep === DealStep.COMPLETE_NDA_FORM)
    if (hasNdaStep) return "/nda"
    return "/deals"
  }

  const handleVerifyOtp = async () => {
    try {
      if (!userId) return;
      await verifyOtpMutate({ userId, otp, userType });
      await refreshUser();
    } catch (err) {
      alert("Invalid or expired code");
    }
  }

  const handleResendOtp = async () => {
    if (!userId) return;
    const sent = await resendOtp({ userId, userType });
    setCodeTarget(codeTargetFrom(sent));
  }

  // After refreshUser completes from OTP flow, determine redirect
  useEffect(() => {
    if (authUser && !pendingRedirect && mfaStep) {
      if (userType === TYPE_DEAL_MANAGER) {
        setPendingRedirect("/eso/deals")
      } else {
        setPendingRedirect(getClientRedirect(authUser as { deals?: { currentStep?: string }[] }))
      }
    }
  }, [authUser])

  return (
    <Flex
      direction="column"
      align="center"
      justify="center"
      minH="100vh"
      position="relative"
      overflow="hidden"
    >
      <Text
        fontSize="xl"
        fontWeight="bold"
        zIndex={1}
        color="white"
        position="absolute"
        top={{ base: 5, md: 10 }}
        left={{ base: 5, md: 10 }}
      >
        ESO Fund Client Portal
      </Text>
      {
        mfaStep ? (
          <MfaComponent
            userId={userId!}
            target={codeTarget}
            otp={otp}
            setOtp={setOtp}
            handleVerifyOtp={handleVerifyOtp}
            handleResendOtp={handleResendOtp}
            isLoading={isPending || isSuccess}
            isResending={isResending}
          />
        ) : (
          <SignInComponent
            email={email}
            setEmail={setEmail}
            handleLogin={handleLogin}
            isLoading={isLoading}
            errorMessage={loginError}
          />
        )
      }
      <HStack gap={{ base: 4, md: 10 }} position="absolute" bottom={10} zIndex={1} flexWrap="wrap" justifyContent="center" px={4}>
        <Text fontSize="sm" fontWeight="bold" color="darkBlue.100">
          © ESO Fund 2025. All rights reserved.
        </Text>
        <Text as={Link} color="brand.25" fontSize="sm" fontWeight="bold" onClick={() => window.open("https://www.esofund.com/privacy-policy", "_blank", "noopener,noreferrer")}>
          Privacy Policy
        </Text>
      </HStack>

      <Box
        position="absolute"
        top={0}
        left={0}
        w="100%"
        h="75vh"
        bgGradient="linear-gradient(to right, #253359, #3F5EA1)"
        transform="skewY(-5deg)"
        transformOrigin="top left"
        zIndex={0}
      />
    </Flex>
  )
}
